import { FlexBox } from 'components/Containers/FlexBox';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

import {
  generateRandomColor,
  lineChartDefaultColorMap,
  darkThemeLineChartDefaultColorMap,
  removeLastItemFromData,
} from 'utils/helpers/general';

import type { LineChartData } from 'types/chart.types';
import { useEffect, useRef } from 'react';
import { useAppSelector } from 'store/hook';
import { selectExportFormat } from 'store/exportData.slice';
import { exportToPdf } from 'utils/exports/pdf';
import { formatDate } from 'views/Settings/ExternalUsers/Squares/Helpers/prepareFile';
import { chartToExcelForVehicleStatus } from 'utils/exports/excel';
import { Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { type RuleCondition } from 'types/notification.types';
import { selectHourlyOrDailyObject } from 'utils/charts/tags';
import { formatYAxis } from 'views/SensorAnalysisView/SensorAnalysisView/Content/Chart/Individual/Chart';
import { toSnakeCase } from 'utils/helpers/string.manipulation';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  data: LineChartData[];
  xAxis: string;
  exportTo?: boolean;
  handleExport: (val: boolean) => void;
  assetBumperNumber: string;
  selectedRange: string;
  device: string;
  avgVal: string;
  enableDarkTheme: boolean;
  xAxisTimeFormat?: string;
  domainValue?: [boolean, string];
  notificationConditions?: RuleCondition[];
}

const LineChartComponent = ({
  data,
  xAxis,
  exportTo,
  handleExport,
  assetBumperNumber,
  selectedRange,
  device,
  avgVal,
  enableDarkTheme,
  xAxisTimeFormat = DATE_FORMAT,
  domainValue,
  notificationConditions,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const uniqueUnitLabels = filterUniqueLabels(data, enableDarkTheme);
  const chartRef = useRef<any>(null);
  const { tagChartData, tags } = useAppSelector((state) => state.tagReducer);

  const dataValues =
    Array.isArray(data) && data.length <= 0
      ? []
      : data.flatMap((item) => {
          return Array.isArray(item.data)
            ? item?.data?.map((dataItem) => dataItem.value)
            : [];
        });
  const minValue = Math.min(...dataValues);
  const maxValue = Math.max(...dataValues);

  const tagsWithNoData = () => {
    const getDataWithEmptyDetails: Array<{ tagName: string; tagDetails: [] }> =
      tagChartData.filter(
        (tag: { tagName: string; tagDetails: []; isValidRange: boolean }) => {
          const tagsDetails = selectHourlyOrDailyObject(
            tag.tagDetails,
            selectedRange
          );
          return tagsDetails.length < 1 || !tag.isValidRange;
        }
      );
    // get reqd day range, get latest ts per tag, check if they fit into the selected day range
    const chartDataList: any[] = [];
    tagChartData.map((data: any) => {
      const tagDetails = selectHourlyOrDailyObject(
        data.tagDetails,
        selectedRange
      );
      const dataTagName = data.tagName;
      const dataTs: [number] = [0];
      tagDetails.map((detail: any) => dataTs.push(detail.ts));
      const maxTs = Math.max(...dataTs);
      const newObj: any = {};
      newObj[dataTagName] = maxTs;
      return chartDataList.push(newObj);
    });

    const tagsWithNoDataResponse: Array<{ tagName: string; tagDetails: [] }> =
      [];
    let unixMsBySelectedRange = 0;

    const getUnixMsBySelectedRange = (dateRange: number) => {
      const d = new Date();
      d.setDate(d.getDate() - dateRange);
      return new Date(d).getTime();
    };
    chartDataList.map((item) => {
      const itemObj = Object.entries(item)[0];
      const itemTs: number = itemObj[1] as number;
      const tagExists = getDataWithEmptyDetails.some(
        (item) => item.tagName === itemObj[0]
      );
      const noDataTags = (msRange: number) => {
        if (itemTs < msRange && !tagExists) {
          tagsWithNoDataResponse.push({
            tagName: itemObj[0],
            tagDetails: [],
          });
        } else {
          tagsWithNoDataResponse.push(...getDataWithEmptyDetails);
        }
      };
      switch (selectedRange) {
        case '1D':
          unixMsBySelectedRange = getUnixMsBySelectedRange(1);
          noDataTags(unixMsBySelectedRange);
          break;
        case '7D':
          unixMsBySelectedRange = getUnixMsBySelectedRange(7);
          noDataTags(unixMsBySelectedRange);
          break;
        case '14D':
          unixMsBySelectedRange = getUnixMsBySelectedRange(14);
          noDataTags(unixMsBySelectedRange);
          break;
        case '1Y':
          unixMsBySelectedRange = getUnixMsBySelectedRange(365);
          noDataTags(unixMsBySelectedRange);
          break;

        default:
          break;
      }
      return unixMsBySelectedRange;
    });

    const dateRangeMax = new Date(unixMsBySelectedRange).toDateString();
    return { getDataWithEmptyDetails, dateRangeMax };
  };

  const exportFormat = useAppSelector(selectExportFormat);

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const fileNameWithDateTime = `Vehicle Status for ${assetBumperNumber}-${formatDate(
    new Date()
  )}`;

  const labels: string[] = [];
  data.map((item) => labels.push(item.name));

  const title = `Vehicle Status for ${assetBumperNumber} ${
    selectedRange === 'ALL'
      ? '(All records)'
      : `between ${new Date().toDateString()} and ${
          tagsWithNoData().dateRangeMax
        }.`
  }`;

  useEffect(() => {
    const fetchData = async () => {
      if (exportTo && exportFormat === 'pdf') {
        await exportToPdf(
          'vehicle-status-page',
          fileNameWithDateTime,
          title,
          enableDarkTheme,
          'landscape'
        );
        handleExport(false);
      }
      if (exportTo && exportFormat === 'excel') {
        chartToExcelForVehicleStatus(
          data,
          fileNameWithDateTime,
          title,
          unitSystem,
          preferredTimeDisplayMode,
          timezone
        );
        handleExport(false);
      }
    };

    void fetchData();
  }, [exportTo, handleExport, exportFormat]);

  const formatValue = (value: any) => {
    if (typeof value === 'number' && value % 1 !== 0) {
      return value.toFixed(2);
    }
    return Number(value).toFixed(2);
  };

  const getTagAlias = (tagName: string, device: string): string => {
    const deviceTags = tags[device]?.tags || [];
    const tag = deviceTags.find((t) => t.tagName === tagName);

    return tag?.tagAlias != null ? tag.tagAlias : tagName;
  };
  const evaluateExpression = (
    value: number,
    operator: string,
    comparisonValue: number
  ) => {
    switch (operator) {
      case '>':
        return value > comparisonValue;
      case '<':
        return value < comparisonValue;
      case '>=':
        return value >= comparisonValue;
      case '<=':
        return value <= comparisonValue;
      case '=':
        return value === comparisonValue; // Use '===' for strict equality if appropriate
      case '!=':
        return value !== comparisonValue; // Use '!==' for strict inequality if appropriate
      default:
        return false;
    }
  };

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;

    const itemNotification = notificationConditions?.find(
      (item) => item.tagName === toSnakeCase(payload.device_sensor)
    );

    if (itemNotification) {
      const isExpression = evaluateExpression(
        +payload.value,
        itemNotification.relationalOperator,
        itemNotification.value
      );
      if (isExpression) {
        return <circle cx={cx} cy={cy} r={1} fill={'red'} stroke="red" />;
      } else {
        return <circle cx={cx} cy={cy} r={1} fill="black" stroke="none" />;
      }
    } else {
      return <circle cx={cx} cy={cy} r={1} fill="orange" stroke="none" />;
    }
  };

  return (
    <FlexBox
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: 'inherit',
        height: '650px',
        flexDirection: 'column',
        paddingTop: '20px',
        marginRight: '10px',
        paddingBottom: '10px',
      }}
      id="vehicle-status-page"
    >
      <ResponsiveContainer
        width="100%"
        height={
          tagsWithNoData().getDataWithEmptyDetails.length >= 1 ? '90%' : '100%'
        }
        ref={chartRef}
      >
        <AreaChart
          width={500}
          height={400}
          margin={{
            top: 1,
            right: 100,
            left: 20,
            bottom: 140,
          }}
          data={data}
        >
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FFA726" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#FFA726" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={palette.cartesianGrid} strokeDasharray="3 3" />
          <XAxis
            dataKey={xAxis}
            domain={['auto', 'auto']}
            scale="time"
            type="number"
            allowDuplicatedCategory={false}
            tickFormatter={(value) =>
              convertUtcToDisplayTime(
                value,
                preferredTimeDisplayMode,
                timezone,
                xAxisTimeFormat
              )
            }
            angle={-65}
            textAnchor="end"
            tick={{ fontSize: 11 }}
            minTickGap={40}
          />
          {uniqueUnitLabels.map((dt: LineChartData, index: number) => {
            if (dt.name !== 'sym_start') {
              return (
                <YAxis
                  key={dt.name}
                  yAxisId={dt.label}
                  domain={
                    domainValue?.[0]
                      ? [minValue, maxValue + 10]
                      : ['auto', 'auto']
                  }
                  stroke={dt.color ?? generateRandomColor()}
                  label={{
                    value: `${getTagAlias(dt.label, device)}`,
                    angle: -90,
                    position: 'inside',
                    fill: enableDarkTheme
                      ? ThemePalette.typography.wheat
                      : ThemePalette.typography.black,
                    dy: 10,
                    dx: 15,
                  }}
                  tickCount={8}
                  tickFormatter={(value) =>
                    (Math.round(formatYAxis(value) / 5) * 5).toString()
                  }
                />
              );
            }
            return (
              <YAxis
                key={dt.name}
                yAxisId={dt.label}
                stroke={dt.color}
                label={{
                  value: `${dt.label}`,
                  angle: -90,
                  position: 'inside',
                  fill: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                  dy: 10,
                  dx: 15,
                }}
                tickCount={8}
                tickFormatter={
                  dt.name === 'sym_start'
                    ? (value) =>
                        convertUtcToDisplayTime(
                          value,
                          preferredTimeDisplayMode,
                          timezone,
                          'YYYY-MM'
                        )
                    : formatNormalAxis
                }
                domain={[new Date('2020-01-01').getTime(), 'auto']}
              />
            );
          })}

          <YAxis
            yAxisId="left"
            orientation="left"
            tickFormatter={(value) => Math.round(value).toString()}
            domain={[0, 'auto']}
            allowDecimals={false}
            tick={{ fontSize: 11 }}
          />

          <Tooltip
            labelFormatter={(value) =>
              convertUtcToDisplayTime(
                value,
                preferredTimeDisplayMode,
                timezone,
                'YYYY MMM DD HH:mm'
              )
            }
            formatter={(value) => formatYAxis(formatValue(value))}
          />
          <Legend
            wrapperStyle={{ fontSize: '20px' }}
            layout="horizontal"
            verticalAlign="top"
            align="center"
          />
          {removeLastItemFromData(data, selectedRange).map(
            (dt: any, index: number) => {
              return (
                <Area
                  key={index}
                  yAxisId={dt.label}
                  type="monotone"
                  dataKey={avgVal}
                  data={dt.data}
                  stroke={
                    enableDarkTheme
                      ? darkThemeLineChartDefaultColorMap[index] ||
                        generateRandomColor()
                      : lineChartDefaultColorMap[index] || generateRandomColor()
                  }
                  fillOpacity={1}
                  fill="url(#colorUv)"
                  strokeWidth={2}
                  dot={(props) => <CustomDot {...props} />}
                />
              );
            }
          )}
        </AreaChart>
      </ResponsiveContainer>

      <Typography
        style={{
          fontSize: '14px',
          color: ThemePalette.typography.warning,
          display:
            tagsWithNoData().getDataWithEmptyDetails.length >= 1 &&
            !notificationConditions
              ? 'block'
              : 'none',
          padding: '0 10%',
          textAlign: 'center',
        }}
      >
        <strong>
          {t('asset_was_not_able_to_transmit_value_during_this_time_period')}
        </strong>
        <br />
        {tagsWithNoData().getDataWithEmptyDetails.map(
          (tag: { tagName: string; tagDetails: [] }, index) => (
            <span key={index} style={{ margin: '5px 0', color: 'red' }}>
              {'\u00A0\u00A0*'} {getTagAlias(tag.tagName, device)}
            </span>
          )
        )}
      </Typography>
    </FlexBox>
  );
};

export default LineChartComponent;

const filterUniqueLabels = (
  data: LineChartData[],
  enableDarkTheme: boolean
) => {
  const mappedLabels = data.map((item, index) => {
    return {
      name: item.name,
      label: item.label,
      data: [],
      color: enableDarkTheme
        ? darkThemeLineChartDefaultColorMap[index]
        : lineChartDefaultColorMap[index],
    };
  });

  const uniqueLabels: any = {};
  const result = [];

  for (const item of mappedLabels) {
    if (!uniqueLabels[item.label]) {
      uniqueLabels[item.label] = true;
      result.push(item);
    }
  }

  return result;
};

function formatNormalAxis(tickItem: any) {
  return tickItem;
}
